import { IntlProvider, FormattedMessage } from 'react-intl';
import { URL } from '@ux/util';
import { default as AllProductsNav } from '@ux/mobile-nav';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useBreakpoints, isMobile } from '@ux/component-utilities';
import { breakpoints, golf } from './enums';
import {
  urlMatches,
  linkListBuilder,
  trayMenusBuilder
} from './utils';

/**
 * Render and expose the Product navigation as a Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced Product Nav.
 * @public
 */
export class ProductNavComponent extends Component {
  constructor(props) {
    super(...arguments);

    this.state = {
      ...this.nextStateFromProps(props)
    };
  }

  /**
   * Initialize current href and active heading.
   *
   * @param {Object} props received
   * @returns {Object} Object containing currentHref and activeHeading
   * @private
   */
  nextStateFromProps(props) {
    let { currentHref, activeCaption } = props;

    if (typeof document !== 'undefined' && !currentHref) {
      activeCaption = window.sessionStorage.getItem('activeCaption');
      currentHref = document.location.href;
    }

    if (currentHref) {
      const currentUrl = new URL(currentHref, true);
      const qsCurrentHref = currentUrl.query.currentPage;
      if (qsCurrentHref) {
        currentHref = qsCurrentHref;
      } else {
        currentHref = currentUrl.href;
      }
    }

    const active = this.findParent({
      url: currentHref,
      id: activeCaption,
      items: props.navigation,
      firstMatch: false
    });

    return {
      currentHref,
      activeHeading: active && active.activeHeading
    };
  }

  /**
   * Perform a recursive search through the navs data structure looking for
   * the specific URL and title. When found return the parent object that contains it
   * so we can get all of it's sibling links.
   *
   * @param {Object} opts Options for findParent
   * @param {String} opts.url The url to match against
   * @param {String} opts.id The title to check against
   * @param {Object[]} opts.items The child items for which to find the parent
   * @param {Object} opts.firstMatch The parent object containing the first matching url
   * @param {Object} [opts.parent] The parent returned from the previous call to findParent
   *
   * @returns {Object} Parent object containing url
   * @private
   */
  findParent(opts) {
    // eslint-disable-line complexity, max-statements
    const { url, id, items, parent } = opts;
    let { firstMatch } = opts;

    if (!url || !items) {
      return;
    }

    if (Array.isArray(items)) {
      for (let i = 0, len = items.length; i < len; i++) {
        const result = this.findParent({
          url,
          id,
          items: items[i],
          firstMatch,
          parent
        });
        if (result) {
          if (result.isFullMatch) {
            return result;
          }
          if (i === len - 1) {
            return result;
          } else if (!firstMatch) {
            firstMatch = result.activeHeading;
          }
        }
      }
    } else {
      if (items.href && urlMatches(url, items.href, true)) {
        // save the first url match in case there is no match
        if (!firstMatch) {
          firstMatch = parent || items;
        }
        if (!id || id === items.id) {
          return {
            isFullMatch: true,
            activeHeading: parent || items
          };
        }
      }

      if (items.children) {
        return this.findParent({
          url,
          id,
          items: items.children,
          firstMatch,
          parent: items
        });
      }

      if (firstMatch) {
        return {
          isFullMatch: false,
          activeHeading: firstMatch
        };
      }
    }
  }

  /**
   * Build the primary navigation links from the passed in nav data structure.
   *
   * @returns {Array} nav items
   * @private
   */
  buildMainNav() {
    const { props } = this;
    const navs = [];
    props.navigation.forEach(item => {
      if (item.title) {
        if (item.children || item.href) {
          if (!item.onlyMobile) {
            navs.push(item);
          }
        }
      }
    });

    return trayMenusBuilder(navs, {
      urlArgs: props.urlArgs,
      className: 'topnav-item',

      classes: {
        isPro: 'flag-pro',
        isFree: 'flag-free',
        isNew: 'flag-new',
        onSale: 'flag-sale',
        tout: 'flag-sale',
        h5: 'h5'
      }
    });
  }

  /**
   * Build the primary navigation links to be displayed on the right side
   * of the header.
   *
   * @param {String} navRightClassName class name for the right navigation
   * @returns {Array} nav items
   * @private
   */
  buildNavRight(navRightClassName) {
    const { props } = this;
    if (!props.navigationRight) {
      return null;
    }

    // navigationRight can have links or menus
    return trayMenusBuilder(props.navigationRight, {
      className: classnames(navRightClassName, 'topnav-item'),

      classes: {
        isPro: 'flag-pro',
        isFree: 'flag-free',
        isNew: 'flag-new',
        onSale: 'flag-sale',
        tout: 'flag-sale',
        h5: 'h5'
      },

      eidPrefix: 'uxp.hyd.product_nav',
      urlArgs: props.urlArgs
    });
  }

  /**
   * Build the secondary navigation links from the passed in nav data structure.
   *
   * @param {number} category index of selected primary nav
   * @returns {Array} nav items
   * @private
   */
  buildSecondaryNav() {
    // eslint-disable-line max-statements, complexity
    const { props, state } = this;
    const { activeHeading: data, currentHref } = state;
    let active;

    if (!data || !data.children) {
      return [];
    }

    // determine which child link is active
    for (let i = 0; i < data.children.length; i++) {
      const item = data.children[i];
      if (urlMatches(currentHref, item.href, true)) {
        if (item.options && item.options.hideSecondaryNav) {
          return [];
        }
        active = item.id;
      }
    }

    const subnav = linkListBuilder(data.children, {
      className: 'secondarynav-item',
      activeClass: 'active',
      active,
      eidPrefix: 'uxp.hyd.secondary_nav',
      urlArgs: props.urlArgs
    });

    const { title, id } = data;
    if (title) {
      subnav.unshift(
        <li
          key={ title }
          id={ id }
          className='secondarynav-item'>
          { title }
        </li>
      );
    }

    return subnav;
  }

  /**
   * Renders the desktop primary & secondary navigation
   *
   * @returns {ReactElement} the desktop nav
   * @private
   */
  renderDesktop() {
    return (
      <section>
        { this.renderDesktopPrimaryNav() }
        { this.renderDesktopSecondaryNav() }
      </section>
    );
  }

  /**
   * Renders the desktop primary navigation
   *
   * @returns {ReactElement} primary navigation
   * @private
   */
  renderDesktopPrimaryNav() {
    const { props } = this;

    if (!props.showPrimaryNav) {
      return null;
    }

    const topNavList = this.buildMainNav();
    const navRight = this.buildNavRight();

    return (
      <nav className='topnav reseller'>
        <div
          className='container'
          ref={ r => {
            this.topNav = r;
          } }>
          <div className='flex-row'>
            <ul
              className='topnav-items'
              ref={ r => {
                this.topNavItems = r;
              } }>
              { topNavList }
            </ul>
            <ul className='topnav-right'>
              { navRight }
            </ul>
          </div>
        </div>
      </nav>
    );
  }

  /**
   * Renders the desktop secondary navigation
   *
   * @returns {ReactElement} secondary navigation
   * @private
   */
  renderDesktopSecondaryNav() {
    const secondaryNavList = this.buildSecondaryNav();

    if (!secondaryNavList.length) {
      return null;
    }

    return (
      <nav className='secondarynav'>
        <div
          className='container'
          ref={ r => {
            this.secondaryNav = r;
          } }>
          <div className='row'>
            <div className='col-sm-12'>
              <ul
                className='secondarynav-items'
                ref={ r => {
                  this.secondaryNavItems = r;
                } }>
                { secondaryNavList }
              </ul>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  /**
   * Renders the mobile navigation
   *
   * @returns {ReactElement} mobile navigation
   * @private
   */
  renderMobile() {
    const { state, props } = this;

    if (!props.showPrimaryNav) {
      return null;
    }

    const navs = [];
    const navRight = this.buildNavRight('topnav-promos-link-mobile');

    props.navigation.forEach(item => {
      // convert `leftnav` to `href` to match expected data structure of `@ux/mobile-nav`
      if (item.leftnav && item.leftnav.href) {
        if (!item.href) {
          item.href = item.leftnav.href;
        }
      }

      if (item.title) {
        navs.push(item);
      }
    });

    return (
      <div>
        <nav
          className={ classnames(
            'topnav',
            'clearfix',
            'reseller'
          ) }
          style={{
            overflow: 'visible'
          }}>
          <div className='container'>
            <div className='row'>
              <div className='flex-row'>
                <AllProductsNav
                  nav={ navs }
                  menuTitle={ <FormattedMessage id={ golf.allProducts } /> }
                  market={ props.market }
                  messages={ props.messages }
                  urls={ props.urls }
                  currentHref={ state.currentHref }
                  activeHeading={ state.activeHeading }
                  showHomeLink={ props.isReseller }
                  eidPrefix='uxp.hyd.product_nav'
                  urlArgs={ props.urlArgs }
                  menuButtonEid={ 'uxp.hyd.all_products_menu_open.button.click' }
                />
                <ul className='mobile-nav-right'>
                  { navRight }
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </div>
    );
  }

  /**
   * Renders complete nav.
   *
   * @returns {ReactElement} The rendered header
   * @private
   */
  render() {
    const { props } = this;
    let content = null;
    if (
      isMobile(props.breakpoint) ||
      props.breakpoint === 'tablet'
    ) {
      content = this.renderMobile();
    } else {
      content = this.renderDesktop();
    }
    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <div className='top'>
          { content }
        </div>
      </IntlProvider>
    );
  }
}

/**
 * Property definitions.
 *
 * @type {Object}
 * @public
 */
ProductNavComponent.propTypes = {
  currentHref: PropTypes.string,
  market: PropTypes.string,
  messages: PropTypes.object.isRequired,
  navigation: PropTypes.array.isRequired,
  navigationRight: PropTypes.array,
  showPrimaryNav: PropTypes.bool,
  showCountryNameOnLogo: PropTypes.bool,
  specialNav: PropTypes.array,
  sticky: PropTypes.object,
  isReseller: PropTypes.bool,
  urlArgs: PropTypes.object,
  urls: PropTypes.object,
  breakpoint: PropTypes.string.isRequired
};

/**
 * Default property values.
 *
 * @type {Object}
 * @public
 */
ProductNavComponent.defaultProps = {
  country: '',
  currentHref: '',
  market: 'en-US',
  navigationRight: [],
  showCountryNameOnLogo: true,
  showPrimaryNav: true,
  isReseller: false
};

export default function ProductNavWrapper(props) {
  const { breakpoint } = useBreakpoints(...breakpoints);
  return <ProductNavComponent breakpoint={ breakpoint } { ...props }/>;
}
